import { Redirect, Route, Switch } from 'react-router-dom';

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ActiveHelper from '../../../components/common/activeHelper';
import ProvidersFilter from '../../../components/common/ProvidersFilter';
import { firestore } from '../../../helpers/Firebase';
import { accessTypes, deviceWidth, exportProviders } from '../../../helpers/Utils';
import Subscriptions from './components/subscriptions';

const Providers = ({ match }) => {
  const { activeItem } = useSelector(state => state.activeItem);
  const { user } = useSelector(state => state.authUser);
  const search = useSelector(state => state.settings.search) || '';
  const { provider } = useSelector(state => state.providers);
  const [filteredProviders, setFilteredProviders] = useState(Object.values(provider || {}));
  const [providersWithRating, setProvidersWithRating] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  // When a service is picked in ProvidersFilter, the Rating column reflects
  // that service's average (with a 3-rating minimum before we prefer it over
  // the global). null = no service filter, show the global rating like before.
  const [serviceFilterId, setServiceFilterId] = useState(null);

  const PER_SERVICE_RATING_MIN_COUNT = 3;

  // Update providers with rating when provider data, analytics, or service filter changes
  useEffect(() => {
    const providersWithRating = Object.values(provider || {}).map(providerItem => {
      const providerId = providerItem.key || providerItem.id;
      const providerAnalytics = analytics?.[providerId];
      // Prefer the per-service bucket when a service is filtered AND it has
      // enough ratings to be trustworthy. Otherwise use the global totalRate.
      const perServiceBucket = serviceFilterId
        ? providerAnalytics?.serviceRatings?.[serviceFilterId]
        : null;
      const usePerService = perServiceBucket && (perServiceBucket.count || 0) >= PER_SERVICE_RATING_MIN_COUNT;
      const rate = usePerService ? (perServiceBucket.rate || 0) : (providerAnalytics?.totalRate?.rate || 0);
      const count = usePerService ? (perServiceBucket.count || 0) : (providerAnalytics?.totalRate?.count || 0);
      const rating = count > 0 ? (rate / count).toFixed(1) : 0;

      return {
        ...providerItem,
        rating: rating
      };
    });
    setProvidersWithRating(providersWithRating);
  }, [provider, analytics, serviceFilterId]);

  // Apply search filter on top of other filters
  useEffect(() => {
    let filtered = providersWithRating;

    // Search filter - search by name, email, phone number, and unique ID
    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      filtered = filtered.filter(prov => {
        const nameMatch = (prov?.name?.toLowerCase() || '').includes(searchLower);
        const emailMatch = (prov?.email?.toLowerCase() || '').includes(searchLower);
        const numberMatch = (prov?.number?.replaceAll(' ', '').toLowerCase() || '').includes(searchLower);
        const uniqueIdMatch = (prov?.uniqueID?.toLowerCase() || '').includes(searchLower);
        return nameMatch || emailMatch || numberMatch || uniqueIdMatch;
      });
    }

    setFilteredProviders(filtered);
  }, [providersWithRating, search]);

  useEffect(() => {
    const unsubscribeAnalytics = firestore().collection('newAnalytics').doc('SP').onSnapshot(analyticsSnap => {
      let data = analyticsSnap?.data()
      setAnalytics(data)
    })

    const unsubscribeAdmins = firestore().collection('admin').onSnapshot(adminSnap => {
      let data = adminSnap?.docs?.reduce((val, currentVal) => {
        val.push({ ...currentVal.data(), id: currentVal.id, key: currentVal.id })
        return val
      }, [])
      setAdmins(data)
    })

    // Cleanup function to unsubscribe listeners
    return () => {
      unsubscribeAnalytics()
      unsubscribeAdmins()
    }
  }, []);

  const elements = useMemo(() => {

    return [{
      key: 'generalInfo',
      title: 'Provider Information',
      hideTitle: true,
      data: [
        { key: 'agent', title: 'Responsible Agent', width: '10', type: 'select', data: admins, hide: !accessTypes.includes(user?.accessType) },
        { type: 'subheader', title: 'Provider Information', hideTitle: true },
        { key: 'pictures', title: 'Provider Picture', width: '10', type: 'picture', dimensions: { width: 720, height: 1080 } },
        { key: 'name', title: 'Name', width: '5', type: 'text' },
        { key: 'name_ar', title: 'Arabic Name', width: '5', type: 'text' },
        { key: 'description', title: 'Details', width: '10', type: 'textarea' },
        { key: 'description_ar', title: 'Arabic Details', width: '10', type: 'textarea' },

        { type: 'subheader', title: 'Account Information', hideTitle: true },
        { key: 'email', title: 'Email', width: '10', type: 'email', showResetPassword: true, showChangeEmail: true },
        { key: 'password', title: 'Password', width: '10', type: 'password', hide: !activeItem?.general?.addNew },
        { key: 'confirmPassword', title: 'Confirm Password', width: '10', type: 'password', hide: !activeItem?.general?.addNew },
        { key: 'number', title: 'Number', width: '10', type: 'phoneNumber', showChangeNumber: false },
        { key: 'callCenter', title: 'Call Center', width: '10', type: 'phoneNumber' },


        { type: 'subheader', title: 'Address Information', hideTitle: true },
        { key: 'map', title: 'Map', width: '10', type: 'map' },
        { key: 'address', title: 'Address', width: '5', type: 'text' },
        { key: 'address_ar', title: 'Arabic Address', width: '5', type: 'text' },
        { key: 'area', title: 'Area', width: '5', type: 'text' },
        { key: 'area_ar', title: 'Arabic Area', width: '5', type: 'text' },
      ]
    },
    {
      key: 'accountInfo',
      title: 'Account Information',
      hideTitle: true,
      // hide: !accessTypes.includes(user?.accessType),
      data: [
        { type: 'subheader', title: 'Trade License Information', hideTitle: true },
        { key: 'tradeLicenseNumber', title: 'Trade License Number', parent: 'tradeLicense', width: '10', type: 'text', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'tradeLicenseExpiryDate', title: 'Trade License Expiry Date', width: '10', type: 'date', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'IDNumber', title: 'ID Number', parent: 'tradeLicense', width: '10', type: 'text', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'IDExpiryDate', title: 'ID Expiry Date', width: '10', type: 'date', disabled: !accessTypes.includes(user?.accessType) },

        { type: 'subheader', title: 'Bank Details', hideTitle: true },
        { key: 'IBAN', title: 'IBAN', parent: 'bankDetails', width: '10', type: 'text', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'accountNumber', title: 'Account Number', parent: 'bankDetails', width: '10', type: 'number', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'accountName', title: 'Account Name', parent: 'bankDetails', width: '10', type: 'text', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'branch', title: 'Bank Name', parent: 'bankDetails', width: '10', type: 'text', disabled: !accessTypes.includes(user?.accessType) },

        { type: 'subheader', title: 'Account Status', hideTitle: true },
        { key: 'joiningDate', title: 'Joining Date', width: '10', type: 'date', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'expiryDate', title: 'Expiry Date', width: '10', type: 'date', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'renewalDate', title: 'Renewal Date', width: '10', type: 'date', disabled: !accessTypes.includes(user?.accessType) },

        { type: 'subheader', title: 'Pricing Details', hideTitle: true },
        { key: 'commissionRate', title: 'Commission Rate', width: '10', type: 'number', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'rejectionRate', title: 'Rejection Rate', width: '10', type: 'number', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'subscriptionFees', title: 'Subscription Fees', width: '10', type: 'number', disabled: !accessTypes.includes(user?.accessType) },
      ]
    },
    {
      key: 'subscriptions', title: 'Subscriptions',
      type: 'standalone',
      isCollection: true,
      data: <Subscriptions
        subscriptions={activeItem?.subscriptions} />
    },

    {
      key: 'sponsorship', title: 'Sponsorship',
      // hide: !accessTypes.includes(user?.accessType),
      data: [
        {
          key: 'sponsorPackage', title: 'Sponsorship package', width: '10', type: 'select',
          disabled: !accessTypes.includes(user?.accessType),
          data: [
            { name: 'Platinum', id: 'platinum', key: 'platinum' },
            { name: 'Gold', id: 'gold', key: 'gold' },
            { name: 'Silver', id: 'silver', key: 'silver' },
          ]
        },
        { key: 'sponsorStartgDate', title: 'Start Date', width: '10', type: 'date', disabled: !accessTypes.includes(user?.accessType) },
        { key: 'sponsorEndDate', title: 'Expiry Date', width: '10', type: 'date', disabled: !accessTypes.includes(user?.accessType) },
      ]
    },
    {
      key: 'vat', title: 'VAT Details',
      data: [
        {
          key: 'vatRegistered', title: 'VAT Registered',
          description: 'Enable if this provider is registered for VAT under their own TRN. Once enabled, TRN, legal name and certificate become required, VAT is calculated on every booking, and a tax invoice is issued to the customer.',
          width: '10', type: 'switch', hideTitle: true,
        },
        {
          key: 'pricesIncludeVat', title: 'Prices Include VAT',
          description: 'If enabled, service prices entered for this provider already include VAT (VAT is extracted from the price). If disabled, VAT is added on top of the entered prices at checkout.',
          width: '10', type: 'switch', hideTitle: true,
          hide: !activeItem?.general?.vatRegistered,
        },
        {
          key: 'legalName', title: 'Legal Entity Name (for tax invoice)', width: '10', type: 'text',
          hide: !activeItem?.general?.vatRegistered,
          required: !!activeItem?.general?.vatRegistered,
        },
        {
          key: 'trnNumber', title: 'Tax Registration Number (TRN)', width: '10', type: 'text',
          hide: !activeItem?.general?.vatRegistered,
          required: !!activeItem?.general?.vatRegistered,
        },
        {
          key: 'vatCertificate', childkey: 'license', title: 'VAT Certificate', width: '10', type: 'dropzone', downloadTemplate: true,
          hide: !activeItem?.general?.vatRegistered,
          required: !!activeItem?.general?.vatRegistered,
        },
      ]
    },
    {
      key: 'settings', title: 'Settings',
      hide: !accessTypes.includes(user?.accessType),
      data: [
        {
          key: 'cashPaymentDisabled', title: 'Disable Cash Payment',
          description: 'If disabled, customers will not be able to pay with cash with this Service Provider',
          width: '10',
          type: 'switch',
          hideTitle: true
        },
        {
          key: 'onsiteServicesEnabled', title: 'Enable On-Site Services',
          description: 'If disabled, on-site services will be hidden for this Service Provider in services and locations',
          width: '10',
          type: 'switch',
          hideTitle: true
        },
        {
          key: 'ppfServicesEnabled', title: 'Enable PPF Services',
          description: 'If disabled, PPF services and recovery service options will be hidden for this Service Provider in locations',
          width: '10',
          type: 'switch',
          hideTitle: true
        },
        {
          key: 'quickWashServicesEnabled', title: 'Enable Quick Wash Services',
          description: 'If disabled, quick wash services will be hidden for this Service Provider in services and locations',
          width: '10',
          type: 'switch',
          hideTitle: true
        },

        // {
        //   key: 'acceptCard', title: 'Accept Card',
        //   description: 'If disabled, customers will not be able to pay with card with this Service Provider',
        //   width: '10', type: 'switch'
        // },
        // {
        //   key: 'acceptApplePay', title: 'Accept Apple Pay',
        //   description: 'If disabled, customers will not be able to pay with Apple Pay with this Service Provider',
        //   width: '10', type: 'switch'
        // },
      ]
    }];
  }, [activeItem?.general, activeItem?.subscriptions, admins, user?.accessType]);

  const tableColumns = useMemo(() => {
    return [
      { Header: "Status", accessor: "active", width: 120, type: 'switch', show: user?.accessType !== 'admin' },
      { Header: "ID", accessor: "uniqueID", width: 100, type: 'label' },
      { Header: "", accessor: "pictures", width: deviceWidth() === 'desktop' ? 120 : 80, type: 'img' },
      { Header: "Name", accessor: "name", width: 150, type: 'label' },
      { Header: "Email", accessor: "email", width: 120, type: 'label' },
      { Header: 'Joining Date', accessor: 'joiningDate', width: 120, type: 'date', show: deviceWidth() === 'desktop' },
      { Header: "Expiry Date", accessor: "expiryDate", width: 120, type: 'date', show: deviceWidth() === 'desktop' },
      { Header: 'Renewal Date', accessor: 'renewalDate', width: 120, type: 'date', show: deviceWidth() === 'desktop' },
      { Header: serviceFilterId ? "Rating (service)" : "Rating", accessor: 'rating', width: 100, type: 'label' },
      { Header: 'Created', accessor: 'created', type: 'date', show: deviceWidth() === 'desktop' },
    ];
  }, [user?.accessType, serviceFilterId]);

  // Export function for service providers
  const handleExportProviders = (data) => {
    exportProviders(filteredProviders, 'ServiceProvidersList.csv');
  };

  // Filter component - updates providersWithRating so search can work on top.
  // Also owns the service filter that toggles the Rating column between the
  // global average and the per-service one.
  const filterComponent = (
    <ProvidersFilter
      admins={admins}
      onFiltersChange={(filteredData) => setProvidersWithRating(filteredData)}
      serviceFilterId={serviceFilterId}
      onServiceFilterChange={setServiceFilterId}
    />
  );

  return (
    <div>
      <Switch>
        <Route path={`${match.url}/`} render={() => {
          return (
            <ActiveHelper
              data={filteredProviders || []}
              title="Service Providers"
              selected='service_provider'
              elements={elements}
              tableColumns={tableColumns}
              defaultSorting="active"
              exportCSV={handleExportProviders}
              filterComponent={filterComponent}
              showCreateButton={accessTypes.includes(user?.accessType)}
            />
          )
        }} />
        <Redirect to="/error" />
      </Switch>
    </div>
  )

};
export default Providers;
